import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ica-navy/40 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary CTA — light-gold fill with white text, deepens to solid
        // gold on hover / press. Used for Save, Create, Update, Add, etc.
        default:
          "bg-ica-gold-light text-white shadow hover:bg-ica-gold hover:text-white active:bg-ica-gold-dark active:text-white",
        // Gold accent — reserved for the ONE hero action per screen
        // (Send-and-save, Publish, etc.). Do not use for generic saves.
        accent:
          "bg-ica-gold text-white font-semibold shadow hover:bg-ica-gold-dark active:bg-ica-gold-dark",
        // Destructive — red (delete, void, wipe).
        destructive:
          "bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800",
        // Secondary — navy framed, no fill, navy text. Solid navy fill with
        // white text on hover / press. Used for Cancel, Export, Template,
        // Upload, and other supporting actions.
        outline:
          "border border-ica-navy bg-white text-ica-navy shadow-sm hover:bg-ica-navy hover:text-white active:bg-ica-navy-dark active:text-white",
        secondary:
          "bg-slate-100 text-ica-navy shadow-sm hover:bg-slate-200 active:bg-slate-300",
        ghost:
          "text-ica-navy hover:bg-ica-navy/5 active:bg-ica-navy/10",
        link:
          "text-ica-navy underline-offset-4 hover:underline hover:text-ica-navy-dark",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
